/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useCallback } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import PropTypes from 'prop-types';
import NoDocumentIcon from 'assets/no_documents_found.svg';
import LandingList from 'components/LandingList';
import SortableTable from 'components/UiKit/SortableTable';
import CwFPagination from 'components/CwFPagination';
import multiLevelSort from 'helpers/multiLevelSort';
import EmptyTableState from 'components/EmptyTableState';
import HistoryIcon from '../../../../../assets/historyIcon.svg';
import useFieldData from '../../../hooks/useFieldData';
import cropHistoryTableSort from './helpers/cropHistoryTableSort';
import { PROPERTY_CROP_HISTORY_TABLE } from './constants';
import PropertyCardHeader from '../PropertyCardHeader';
import combineCropData from './helpers/combineCropData';
import PropertyCropHistoryFullCell from './components/PropertyCropHistoryFullRow';
import useCropHistoryData from './hooks/useCropHistoryData';
import PropertyCropHistorySummaryCell from './components/PropertyCropHistorySummaryRow';
import addVarietyRow from './helpers/addVarietyRow';
import handleDirection from './helpers/sortTables';

const {
  summaryCropHistoryColumnsDefaultSort,
  fullCropHistoryColumnsDefaultSort,
  renderLandingTableSummaryCropHistoryColumns,
  renderLandingTableFullCropHistoryColumns
} = PROPERTY_CROP_HISTORY_TABLE;

const PropertyCropHistory = ({ id: fieldId, level = 'field' }) => {
  const [sortField, setSortField] = useState(
    summaryCropHistoryColumnsDefaultSort.by
  );
  const [sortDir, setSortDir] = useState(
    summaryCropHistoryColumnsDefaultSort.dir
  );

  const { field } = useFieldData(fieldId);
  const [cropHistory, setCropHistory] = useState([]);
  const [cropVariety, setCropVariety] = useState([]);
  const [toggleButtonEnabled, setToggleButtonEnabled] = useState(false);

  const {
    isLoadingCropHistory,
    cropHistoryData,
    isLoadingVariety,
    varietyData,
    resetVariety,
    currentPage,
    setCurrentPage,
    getCropHistory,
    resolvedCropVariety
  } = useCropHistoryData();

  const handlePageChange = page => {
    setToggleButtonEnabled(false);
    resetVariety();
    setCurrentPage(page);
  };

  useEffect(() => {
    if (!toggleButtonEnabled) return;
    resolvedCropVariety();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleButtonEnabled]);

  useEffect(() => {
    if (!varietyData?.length || isLoadingVariety) return;
    setCropVariety(
      multiLevelSort({
        items: addVarietyRow(
          combineCropData(cropHistoryData?.results, varietyData)
        ),
        sortBy: fullCropHistoryColumnsDefaultSort.by,
        sortDir: renderLandingTableFullCropHistoryColumns.dir,
        sorter: cropHistoryTableSort
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingVariety]);

  useEffect(() => {
    if (!cropHistoryData?.results?.length || isLoadingCropHistory) return;
    setCropHistory(
      multiLevelSort({
        items: cropHistoryData?.results,
        sortBy: summaryCropHistoryColumnsDefaultSort.by,
        sortDir: summaryCropHistoryColumnsDefaultSort.dir,
        sorter: cropHistoryTableSort
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCropHistory]);

  const handleSort = useCallback(
    (tableField, dir = 'asc') => {
      handleDirection(
        tableField,
        dir,
        toggleButtonEnabled,
        setCropVariety,
        setCropHistory,
        setSortField,
        setSortDir,
        cropHistoryTableSort
      );
    },
    [toggleButtonEnabled]
  );

  useEffect(() => {
    getCropHistory(currentPage, fieldId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, currentPage]);

  const isToggleButtonEnabled = () => {
    return toggleButtonEnabled;
  };

  const isCropHistoryLoading = () => {
    return !isLoadingCropHistory;
  };

  const _isLoadingVariety = () => {
    return isLoadingVariety;
  };

  // Both views scrolled in a 330px body once they held more than five rows.
  const cropHistoryScrollY = rows => (rows.length > 5 ? 330 : undefined);

  const renderLandingTableSummaryCropHistory = () => (
    <SortableTable
      key={currentPage}
      data-testid="crop-history-table-summary"
      columns={renderLandingTableSummaryCropHistoryColumns}
      rows={cropHistory || []}
      sortBy={sortField[0]}
      sortDir={sortDir[0]}
      onSort={handleSort}
      scrollY={cropHistoryScrollY(cropHistory || [])}
      renderCell={(column, entry) => (
        <PropertyCropHistorySummaryCell field={column} data={entry} />
      )}
    />
  );

  const renderLandingTableFullCropHistory = () =>
    cropVariety?.length ? (
      <div style={{ width: '100%' }}>
        <SortableTable
          key={currentPage}
          sticky
          scrollX={1285}
          scrollY={cropHistoryScrollY(cropVariety)}
          data-testid="crop-history-table-full"
          columns={renderLandingTableFullCropHistoryColumns}
          rows={cropVariety}
          sortBy={sortField[0]}
          sortDir={sortDir[0]}
          onSort={handleSort}
          renderCell={(column, entry) => (
            <PropertyCropHistoryFullCell field={column} data={entry} />
          )}
        />
      </div>
    ) : (
      // What LandingTable showed for an empty list, minus its "Start here"
      // link, which pointed at a bare /create route.
      <LandingList.LandingEmpty listType="Crop History" />
    );

  const renderTableContent = () => {
    if (_isLoadingVariety()) {
      return <LoadingSpinner />;
    }

    if (!isToggleButtonEnabled()) {
      return renderLandingTableSummaryCropHistory();
    }

    return renderLandingTableFullCropHistory();
  };

  const renderContent = () => {
    if (isCropHistoryLoading()) {
      return cropHistory.length > 0 ? (
        <div
          className={`${
            isToggleButtonEnabled()
              ? 'cwf-property-sticky-table'
              : 'cwf-property-not-sticky'
          } min-w-content`}
        >
          {renderTableContent()}
          {CwFPagination(
            cropHistoryData.totalElements,
            currentPage,
            handlePageChange,
            undefined,
            undefined,
            { toggleButtonEnabled, setToggleButtonEnabled }
          )}
        </div>
      ) : (
        <EmptyTableState
          title="No Crop History Found"
          overlayIcon={NoDocumentIcon}
        />
      );
    }

    return <LoadingSpinner message="" label="Loading crop history" />;
  };

  return (
    <div>
      <PropertyCardHeader
        title="Crop History"
        description={`All crops ever planted on field ${field?.name}`}
        icon={HistoryIcon}
      />
      {renderContent()}
    </div>
  );
};
PropertyCropHistory.propTypes = {
  id: PropTypes.string.isRequired,
  level: PropTypes.string.isRequired
};

export default PropertyCropHistory;
